<!--
  Total Breakdown — the owner's two books, on one screen, never added
  together.

  He keeps two. TRACKED is what he costed his crew's Daily Reports out at, at
  his own rates: "what we think we are paying." BANKED is what actually moved
  through the bank account, out of imported statements. Both are true, neither
  is a second opinion on the other, and the whole reason this screen was
  rebuilt is that they were once summed — a poured-and-costed load of cement
  and the card payment that later paid for it, landing in one total, counting
  the same cement twice.

  So the screen is four sections, and every one of them says in its subtitle
  which book it is reading and over what period:

    Tracked                      WHAT WE COSTED       · the picked period
    Bank statements              WHAT ACTUALLY MOVED  · the picked period
    The two books, side by side  TRACKED AGAINST BANK · ALL TIME
    Which jobs are doing best    ALL TIME · EVERY JOB

  The last two say ALL TIME in words because they deliberately ignore the
  month picker sitting above them: money reaches the bank weeks after the
  concrete does, so a job read inside a single month looks unpaid every time.
  A reader has to be able to see that from the heading alone.

  The two books are held in separate `$state` variables named for which book
  they are — `banked`/`bankedAllTime` and `tracked`/`trackedAllTime` — and are
  never merged into one object. The only place they meet is `compareJob`,
  which returns them as two labelled halves and subtracts one from the other;
  nothing on this screen adds across them.

  The three new sections live in ./breakdown as components of their own. This
  file was already long, and a file where all four sections' sourcing has to
  be checked at once is a file where the sourcing stops being checked.

  Three things the bank half shows rather than hides, each for the same reason
  — a figure that quietly omits something is worse than no figure:

  - Untagged money always has a bar, and says so in words above the chart once
    it passes a fifth of the period — company-wide, whether or not a job site
    is picked, because it is a statement about how the importing is going and
    not about the site being looked at. A large untagged share means job
    tagging has slipped, and that is only a signal if it is visible.
  - Transfers sit behind a disclosure headed with their own total. Money moved
    between the owner's own accounts is not money spent; counting a card
    payment would double everything the card already bought. But it did leave
    an account, so it is never simply dropped.
  - An empty period is a sentence and a way forward, not a screen of zeros. A
    grid of $0.00 reads as "the company did nothing" rather than "no statement
    covers this yet". The same rule runs through the compare section: a job
    with no bank lines shows dashes, because nothing tagged is not the same as
    nothing spent.

  Both scope choices live in the URL (?month= / ?year=, plus &site=), so any
  view here is a link that can be bookmarked or sent. But every heading and
  figure is labelled from the response that produced it, never from that URL —
  and each of the three fetches labels from ITS OWN response, because they can
  land in any order and fail independently. On a screen whose whole job is
  being trustworthy about money, a stale figure under a fresh heading is the
  one failure mode that must not be possible.

  Two different dollar figures share the bank half and are never given the
  same word: money in and money out are AS PRINTED on the statement, GST
  included, because that is what left the account; everything the charts
  compare, and the net, are ex-GST, because GST is collected for the
  government rather than earned. The GST band says so out loud. The compare
  section is ex-GST on both sides for the same reason — the tracked book has
  no GST in it either.

  Look: the admin hero and the card surfaces of the rest of the area, with the
  charts from ./charts. Those components never format — every figure they show
  is a `money()` string built by the section that owns them, so a number on a
  chart and the same number in the table are character-for-character
  identical.
-->
<script lang="ts">
  import { api } from '../../lib/api';
  import { compareJob } from '../../lib/calc/compare';
  import { money, moneyAxis, percent } from '../../lib/money';
  import { linkHandler, location, navigate, routePath, siteIdFromQuery } from '../../lib/router';
  import { notifyError } from '../../lib/store';
  import type {
    AdminSiteRow,
    AdminSitesResponse,
    BankedFigures,
    BreakdownLine,
    BreakdownResponse,
    JobComparison,
    SiteTotal,
    TrackedFigures,
    TrackedMonth
  } from '../../lib/types';
  import AdminHero from './AdminHero.svelte';
  import CompareSection from './breakdown/CompareSection.svelte';
  import JobComparisonSection from './breakdown/JobComparisonSection.svelte';
  import TrackedSection from './breakdown/TrackedSection.svelte';
  import BarChart from './charts/BarChart.svelte';
  import ChartLegend from './charts/ChartLegend.svelte';
  import DonutChart from './charts/DonutChart.svelte';
  import { periodLabel, readPeriod, shortMonthLabel, todayIso, type PeriodScope } from './scope';
  import './admin-look.css';

  /*
   * Two books, four variables, and they are never merged into one object.
   *
   * `banked` and `bankedAllTime` are what MOVED THROUGH THE BANK — imported
   * statements and nothing else. `tracked` and `trackedAllTime` are what the
   * crew's Daily Reports were COSTED AT, at the owner's own rates. The names
   * say which book everywhere they appear, because the one bug this screen
   * was rebuilt to remove is the two being added together.
   *
   * The only place they ever meet is `compareJob`, which hands them back as
   * two labelled halves and one subtraction. Nothing below sums across them.
   */

  /** Banked, the picked period. The bank half of the screen reads this. */
  let banked = $state<BreakdownResponse | null>(null);
  let loading = $state(true);
  let loadFailed = $state(false);
  let reloadNonce = $state(0);

  /**
   * Tracked, the picked period — held with the scope it was ASKED for, not
   * just its months. Three fetches feed this screen and they land in any
   * order; a section taking its heading from the shared scope while showing
   * an older fetch's figures would print the wrong period over real money.
   * Each section labels from its own load instead.
   */
  interface TrackedLoad {
    period: PeriodScope;
    siteId: number | null;
    months: TrackedMonth[];
  }
  let tracked = $state<TrackedLoad | null>(null);
  let trackedLoading = $state(true);
  let trackedFailed = $state(false);

  /** Tracked, all time: every job, every Daily Report ever filed against it. */
  let trackedAllTime = $state<AdminSitesResponse | null>(null);
  /** Banked, all time: the same jobs' whole recorded life in the bank. */
  let bankedAllTime = $state<BreakdownResponse | null>(null);
  let allTimeLoading = $state(true);
  let allTimeFailed = $state(false);

  /** What the picker is asking for. Drives the controls and the fetch. */
  const period = $derived(readPeriod($location.query));
  const siteId = $derived(siteIdFromQuery($location.query));

  /*
   * What actually ARRIVED — and what every heading and figure is labelled
   * from. The two are not the same thing for as long as a request is in
   * flight, or after one fails, and the difference is the whole point: a
   * heading read off the URL would print "2026" over August's numbers the
   * moment a Year request failed and left the old response on screen. This is
   * a field app with an offline queue; a failed request is routine, not
   * exceptional. Labelling from the response makes the agreement structural
   * rather than something the fetch has to remember to maintain.
   */
  // `banked.scope` can now also be `{ to_date: true }` (see BreakdownResponse) —
  // a shape this screen does not offer yet, so it is treated the same as "no
  // response has landed" rather than given a PeriodScope it cannot satisfy.
  const loadedPeriod: PeriodScope | null = $derived(banked && !('to_date' in banked.scope) ? banked.scope : null);
  const loadedSiteId = $derived(banked?.site_id ?? null);
  const loadedLabel = $derived(periodLabel(loadedPeriod ?? period));

  /*
   * Two questions that look like one and must not be collapsed back into one:
   * what the owner just ASKED for, and what has actually LOADED. They disagree
   * for as long as a request is in flight, and the two halves of this screen
   * want opposite answers.
   *
   * The scope controls want `asked`: a toggle has to light up on the click,
   * not when the network gets round to it, or the segmented switch and the
   * date field beside it show two different modes at once. (The date field
   * reads `'year' in period` directly rather than this flag — same question,
   * written in the form that narrows the type so `period.year` can be read.)
   *
   * Everything else wants `loaded`: a heading has to describe the figures
   * underneath it, and "2026" printed over August's numbers is precisely the
   * failure this screen exists not to have.
   */
  const askedIsYear = $derived('year' in period);
  const loadedIsYear = $derived('year' in (loadedPeriod ?? period));

  /*
   * A monotonic ticket per request. Two scope changes in quick succession can
   * resolve out of order, and the loser landing last would leave the earlier
   * period's figures under the later one's heading — so only the newest ticket
   * is ever allowed to write.
   */
  let requestSeq = 0;

  $effect(() => {
    const askedPeriod = period;
    const askedSite = siteId;
    // Read so Retry, which bumps it, re-runs this effect.
    void reloadNonce;

    const ticket = ++requestSeq;
    loading = true;
    api
      .getBreakdown(askedPeriod, askedSite)
      .then((response) => {
        if (ticket !== requestSeq) return;
        banked = response;
        loadFailed = false;
      })
      .catch((cause) => {
        if (ticket !== requestSeq) return;
        // Cleared, not kept: figures from the period the owner has already
        // navigated away from are worse than none, because nothing on screen
        // would say they are the wrong ones.
        banked = null;
        loadFailed = true;
        notifyError(cause, 'Could not load the breakdown');
      })
      .finally(() => {
        if (ticket === requestSeq) loading = false;
      });
  });

  /*
   * The Tracked section's own request, kept apart from the bank one above
   * rather than folded into it. They are different routes reading different
   * books, either can fail on its own, and a failed costing must not blank
   * the bank figures — or the reverse. Same ticket discipline, its own
   * counter, and it records the scope it asked for so the section can label
   * itself from what actually landed.
   */
  let trackedSeq = 0;

  $effect(() => {
    const askedPeriod = period;
    const askedSite = siteId;
    void reloadNonce;

    /*
     * `to` is always passed. The route 422s without it, deliberately, so that
     * a chart is never drawn for a period nobody asked for — and the screen
     * always knows which period that is. A year asks for its own twelve
     * months by ending at December; a month asks for the twelve ending at it,
     * matching the bank trend directly below.
     */
    const to = 'year' in askedPeriod ? `${askedPeriod.year}-12` : askedPeriod.month;

    const ticket = ++trackedSeq;
    trackedLoading = true;
    api
      .getTrackedMonths(to, 12, askedSite)
      .then((response) => {
        if (ticket !== trackedSeq) return;
        tracked = { period: askedPeriod, siteId: askedSite, months: response.months };
        trackedFailed = false;
      })
      .catch(() => {
        if (ticket !== trackedSeq) return;
        // Cleared, and NOT raised as a toast: three requests feed this screen
        // and one dead connection would otherwise throw three banners for one
        // fault. The section says its own figures did not arrive, in its own
        // place on the screen, which is where the reader is looking.
        tracked = null;
        trackedFailed = true;
      })
      .finally(() => {
        if (ticket === trackedSeq) trackedLoading = false;
      });
  });

  /*
   * The all-time pair, for the two sections that ignore the month picker.
   * Fetched together because they are read together: the compare block and
   * the register need both books present to say anything at all, and half a
   * comparison is worse than none. The period is deliberately not read here —
   * only the picked job — which is why this effect does not re-run when the
   * month changes.
   */
  let allTimeSeq = 0;

  $effect(() => {
    const askedSite = siteId;
    void reloadNonce;

    const ticket = ++allTimeSeq;
    allTimeLoading = true;
    Promise.all([api.adminSites(), api.getBreakdownToDate(askedSite)])
      .then(([sites, bank]) => {
        if (ticket !== allTimeSeq) return;
        trackedAllTime = sites;
        bankedAllTime = bank;
        allTimeFailed = false;
      })
      .catch(() => {
        if (ticket !== allTimeSeq) return;
        trackedAllTime = null;
        bankedAllTime = null;
        allTimeFailed = true;
      })
      .finally(() => {
        if (ticket === allTimeSeq) allTimeLoading = false;
      });
  });

  function retry() {
    reloadNonce += 1;
  }

  /* --- the scope, which lives in the URL --------------------------------- */

  /*
   * The month last looked at, so Month -> Year -> Month lands back on it
   * rather than on January. Component state rather than the module state
   * ScopePicker keeps: this screen is never unmounted while its scope
   * changes, and a reload should simply start on this month again.
   */
  let lastMonth = $state<string | null>(null);
  $effect(() => {
    if ('month' in period) lastMonth = period.month;
  });

  function go(next: PeriodScope, site: number | null = siteId) {
    navigate(routePath.adminBreakdown(next, site ?? undefined), { replace: true, keepScroll: true });
  }

  function toMonth() {
    const year = 'year' in period ? period.year : period.month.slice(0, 4);
    const remembered = lastMonth && lastMonth.startsWith(`${year}-`) ? lastMonth : null;
    const thisMonth = todayIso().slice(0, 7);
    go({ month: remembered ?? (thisMonth.startsWith(`${year}-`) ? thisMonth : `${year}-01`) });
  }

  function toYear() {
    go({ year: 'year' in period ? period.year : period.month.slice(0, 4) });
  }

  function setMonth(value: string) {
    if (/^\d{4}-\d{2}$/.test(value)) go({ month: value });
  }

  function setYear(value: string) {
    if (/^\d{4}$/.test(value)) go({ year: value });
  }

  function setSite(value: string) {
    const asNumber = Number(value);
    go(period, Number.isInteger(asNumber) && asNumber > 0 ? asNumber : null);
  }

  /* --- figures ----------------------------------------------------------- */

  /*
   * Profit is dollars, margin is the percent — never the same word for both.
   * `percent` lives in ../../lib/money beside `money` now that four sections
   * on this screen print one: a dash for null rather than "0%" is a rule
   * about honesty, and a rule kept in four places is a rule about to be
   * broken in one of them.
   */

  /**
   * Net as a share of what came in. Ex-GST on both sides, so it is a margin on
   * earnings rather than on the GST that was only ever passing through. Null,
   * not 0%, when nothing came in: those are different claims.
   */
  const marginPct = $derived.by(() => {
    if (!banked || banked.income_ex_gst === 0) return null;
    return (banked.net / banked.income_ex_gst) * 100;
  });

  const stats = $derived.by(() => {
    if (!banked) return undefined;
    return [
      // Every label says `bank`: the hero sits above a screen that now also
      // carries costed figures, and a bare "money in" at the top of it would
      // be read as the screen's money in rather than the bank's.
      { value: money(banked.income_total), label: 'money in · bank' },
      { value: money(banked.spend_total), label: 'money out · bank' },
      {
        value: money(banked.net),
        label: 'net · bank, ex-GST',
        tone: banked.net < 0 ? ('bad' as const) : ('plain' as const)
      },
      {
        value: percent(marginPct),
        label: 'margin · bank',
        tone: (marginPct ?? 0) < 0 ? ('bad' as const) : ('plain' as const)
      }
    ];
  });

  /** Nothing at all in the period — not one deposit, payment or transfer. */
  const nothingHere = $derived(banked !== null && banked.lines.length === 0);

  const siteName = $derived(
    loadedSiteId === null
      ? 'Company (all)'
      : banked?.by_site.find((s) => s.site_id === loadedSiteId)?.name ?? 'This site'
  );

  /* --- where the money went --------------------------------------------- */

  /*
   * Transfers are dropped here and shown in their own disclosure instead. The
   * ordered list handed to the donut and to the legend is the SAME array, so
   * a category wears one colour in both — see the ramp note in admin-look.css.
   */
  const spendCategories = $derived(banked ? banked.by_category.filter((c) => c.kind === 'expense') : []);

  const categoryKey = (categoryId: number | null) => (categoryId === null ? 'none' : String(categoryId));

  const slices = $derived(
    spendCategories.map((c) => ({
      key: categoryKey(c.category_id),
      label: c.category,
      value: c.total,
      display: money(c.total)
    }))
  );

  /** The category the donut or the legend has selected, filtering the ledger. */
  let selectedCategory = $state<string | null>(null);

  // A category that stops existing when the scope changes must not go on
  // silently filtering a table nobody can see the filter for.
  $effect(() => {
    if (selectedCategory !== null && !slices.some((s) => s.key === selectedCategory)) {
      selectedCategory = null;
    }
  });

  const selectedCategoryName = $derived(
    slices.find((s) => s.key === selectedCategory)?.label ?? null
  );

  /* --- the trend --------------------------------------------------------- */

  /*
   * The month tick label is `shortMonthLabel` in ./scope now. Two charts on
   * this screen label the same twelve months — one from the bank, one from
   * the Daily Reports — and they sit one above the other; two copies of that
   * function would eventually disagree about whether August is 'Aug'.
   */

  const SERIES = [
    { key: 'income', label: 'Money in' },
    { key: 'spend', label: 'Money out' }
  ] as const;

  const trendGroups = $derived(
    (banked?.by_month ?? []).map((point) => ({
      key: point.month,
      label: shortMonthLabel(point.month),
      bars: [
        { series: 'income', value: point.income, display: money(point.income) },
        { series: 'spend', value: point.spend, display: money(point.spend) }
      ],
      line: point.net,
      lineDisplay: money(point.net)
    }))
  );

  /* --- by job site ------------------------------------------------------- */

  /*
   * Every site the company has would mean a row of empty bars, so a site with
   * nothing in this period is left out — except Untagged, which is always
   * drawn even at zero. It is the one bar whose absence would be read as good
   * news rather than as "nothing was tagged this period".
   */
  const siteRows = $derived(
    (banked?.by_site ?? []).filter((s) => s.site_id === null || s.income !== 0 || s.spend !== 0)
  );

  const siteGroups = $derived(
    siteRows.map((row) => ({
      key: row.site_id === null ? 'untagged' : String(row.site_id),
      label: row.name,
      bars: [
        { series: 'income', value: row.income, display: money(row.income) },
        { series: 'spend', value: row.spend, display: money(row.spend) }
      ],
      line: row.net,
      lineDisplay: money(row.net)
    }))
  );

  /** Sites for the picker, from the same list — no second fetch to disagree with. */
  const pickableSites = $derived((banked?.by_site ?? []).filter((s) => s.site_id !== null));

  /*
   * The untagged share is a statement about the COMPANY's import hygiene, not
   * about whichever site is being looked at — so it is shown whether or not a
   * site is picked, and always worded company-wide.
   *
   * The server's `untagged_pct` cannot answer that on its own: it is computed
   * over the site-scoped rows, and under `?site=3` every surviving row carries
   * site 3 by construction, so it is always 0 and the warning could never
   * fire. Silence there carries no information but reads as "tagging is fine",
   * while the Untagged bar sits in the chart just below saying otherwise. So
   * when a site is picked the share is derived instead from `by_site`, which
   * is deliberately never site-filtered and is ex-GST on both sides — the same
   * numerator and denominator the server uses, just company-wide.
   */
  const untaggedShare = $derived.by(() => {
    if (!banked) return null;
    if (loadedSiteId === null) return banked.untagged_pct;
    const dollars = banked.by_site.reduce((sum, row) => sum + row.income + row.spend, 0);
    if (dollars === 0) return null;
    const untagged = banked.by_site.find((row) => row.site_id === null);
    if (!untagged) return null;
    return ((untagged.income + untagged.spend) / dollars) * 100;
  });
  const untaggedIsLoud = $derived(untaggedShare !== null && untaggedShare > 20);

  /* --- the ledger -------------------------------------------------------- */

  type SortKey = 'date' | 'amount';
  let sortKey = $state<SortKey>('date');
  let sortDesc = $state(true);

  function sortBy(key: SortKey) {
    if (sortKey === key) sortDesc = !sortDesc;
    else {
      sortKey = key;
      sortDesc = true;
    }
  }

  function ariaSort(key: SortKey): 'ascending' | 'descending' | 'none' {
    if (sortKey !== key) return 'none';
    return sortDesc ? 'descending' : 'ascending';
  }

  /**
   * Signed the way the Amount column prints it: a deposit adds, a payment
   * subtracts. Sorting on the unsigned figure would put −$500.00 above
   * +$100.00 under a descending arrow, which reads as a broken control rather
   * than as a different definition of 'largest'.
   */
  function signedAmount(line: BreakdownLine): number {
    return line.kind === 'income' ? line.amount_total : -line.amount_total;
  }

  function sorted(rows: readonly BreakdownLine[]): BreakdownLine[] {
    const direction = sortDesc ? -1 : 1;
    return [...rows].sort((a, b) => {
      if (sortKey === 'amount') return (signedAmount(a) - signedAmount(b)) * direction;
      if (a.date === b.date) return (a.id - b.id) * direction;
      return (a.date < b.date ? -1 : 1) * direction;
    });
  }

  const spendAndIncome = $derived((banked?.lines ?? []).filter((l) => l.kind !== 'transfer'));
  /*
   * Fixed newest-first, and deliberately NOT run through `sorted()`: the
   * disclosure's own headers offer no sort affordance and carry no aria-sort,
   * so re-ordering it from the main ledger's controls would silently rearrange
   * a table nothing on screen said was sortable.
   */
  const transferLines = $derived(
    (banked?.lines ?? [])
      .filter((l) => l.kind === 'transfer')
      .sort((a, b) => (a.date === b.date ? b.id - a.id : a.date < b.date ? 1 : -1))
  );

  /*
   * A selected category filters to that category's PAYMENTS. Deposits carry no
   * category at all, so they leave rather than pile up under a heading that
   * does not describe them.
   */
  const ledgerRows = $derived(
    sorted(
      selectedCategory === null
        ? spendAndIncome
        : spendAndIncome.filter((l) => l.kind === 'expense' && categoryKey(l.category_id) === selectedCategory)
    )
  );

  /* --- tracked: the picked period ---------------------------------------- */

  /*
   * Labelled from the TRACKED fetch's own scope, never from the shared picker
   * and never from the bank response: the two land independently, and a
   * heading reading "2026" over August's costed figures is the failure this
   * screen exists not to have.
   */
  const trackedPeriod = $derived(tracked?.period ?? period);
  const trackedLabel = $derived(periodLabel(trackedPeriod));
  const trackedIsYear = $derived('year' in trackedPeriod);
  const trackedScopeName = $derived(
    tracked === null || tracked.siteId === null ? 'Company (all)' : jobName(tracked.siteId)
  );

  /**
   * A job's name, from whichever book has already answered. The all-time site
   * list is the fuller of the two — it holds every job, including one no bank
   * line has ever mentioned — so it is asked first.
   */
  function jobName(id: number): string {
    return (
      trackedAllTime?.sites.find((site) => site.id === id)?.name ??
      banked?.by_site.find((row) => row.site_id === id)?.name ??
      'This job'
    );
  }

  /**
   * The period's costed totals, summed out of the very months the chart
   * draws: a year is all twelve, a month is the last of the twelve, because
   * the run was asked to END at the month picked. Taken from the same array
   * rather than from a second request, so the figures above the chart and the
   * last bar in it cannot disagree.
   */
  const trackedFigures = $derived.by((): TrackedFigures | null => {
    const months = tracked?.months ?? [];
    if (months.length === 0) return null;
    const inPeriod = trackedIsYear ? months : months.slice(-1);
    const revenue = inPeriod.reduce((sum, month) => sum + month.revenue, 0);
    const cost = inPeriod.reduce((sum, month) => sum + month.cost, 0);
    const margin = inPeriod.reduce((sum, month) => sum + month.margin, 0);
    return {
      revenue,
      cost,
      margin,
      // Null, never 0%: a period with cost and no pour has no revenue to be a
      // percentage OF, and 0% would read as breaking even.
      margin_pct: revenue === 0 ? null : (margin / revenue) * 100
    };
  });

  /* --- all time: the two books beside each other --------------------------- */

  /*
   * ALL TIME comes off the RESPONSE, exactly as every other heading on this
   * screen does. The server signals a whole-life scope with the exact shape
   * `{ to_date: true }` on `scope` — the same key the bank half above tests
   * to recognise a scope it cannot render — so the two all-time sections say
   * ALL TIME because the figures under them ARE all-time, not because this
   * file happened to send the request that asks for them. If a response ever
   * comes back carrying a month or a year instead, the heading says so and
   * the reader is not lied to.
   *
   * Before anything lands there are no figures on screen to mislabel, so the
   * heading describes what was asked for — the same fallback `loadedLabel`
   * makes for the bank half.
   */
  const allTimeLabel = $derived.by(() => {
    if (bankedAllTime === null || 'to_date' in bankedAllTime.scope) return 'All time';
    return periodLabel(bankedAllTime.scope);
  });

  const allTimeSiteId = $derived(bankedAllTime?.site_id ?? null);
  const allTimeScopeName = $derived(allTimeSiteId === null ? 'Company (all)' : jobName(allTimeSiteId));

  /*
   * Which jobs have any bank line at all — counted from the lines, never read
   * off a $0.00 total, because those are different claims. A job nothing has
   * been tagged to has not spent nothing; it has nothing to compare, and the
   * compare section must show it as a dash. Transfers do not count: they are
   * not spending, and the totals exclude them too.
   */
  const jobsWithBankLines = $derived.by(() => {
    const ids = new Set<number>();
    for (const line of bankedAllTime?.lines ?? []) {
      if (line.kind !== 'transfer' && line.job_site_id !== null) ids.add(line.job_site_id);
    }
    return ids;
  });

  /** A job nothing has ever been filed against has nothing costed — not zero. */
  function trackedOf(site: AdminSiteRow): TrackedFigures | null {
    if (site.first_report_date === null) return null;
    return { revenue: site.revenue, cost: site.cost, margin: site.margin, margin_pct: site.margin_pct };
  }

  /*
   * The property names change on the way across — revenue/cost/margin become
   * money_in/money_out/net — and that is the point rather than an
   * inconvenience: the two shapes are deliberately unaddable, so a stray sum
   * reads wrong at a glance. See src/lib/calc/compare.ts.
   */
  function bankedOf(row: SiteTotal | undefined): BankedFigures | null {
    return row ? { money_in: row.income, money_out: row.spend, net: row.net } : null;
  }

  /** Every job's costed figures added up. Tracked only — no bank line is in it. */
  const companyTracked = $derived.by((): TrackedFigures | null => {
    const sites = (trackedAllTime?.sites ?? []).filter((site) => site.first_report_date !== null);
    if (sites.length === 0) return null;
    const revenue = sites.reduce((sum, site) => sum + site.revenue, 0);
    const cost = sites.reduce((sum, site) => sum + site.cost, 0);
    const margin = sites.reduce((sum, site) => sum + site.margin, 0);
    return { revenue, cost, margin, margin_pct: revenue === 0 ? null : (margin / revenue) * 100 };
  });

  /**
   * One row per job, all time, both books. Company scope only: with a job
   * picked there is one row, and the two-column block above already is it.
   */
  const jobRegister = $derived.by((): JobComparison[] => {
    if (allTimeSiteId !== null || bankedAllTime === null || trackedAllTime === null) return [];
    const bankBySite = new Map<number | null, SiteTotal>(
      bankedAllTime.by_site.map((row) => [row.site_id, row])
    );
    return trackedAllTime.sites.map((site) =>
      compareJob(
        trackedOf(site),
        jobsWithBankLines.has(site.id) ? bankedOf(bankBySite.get(site.id)) : null,
        site.id,
        site.name
      )
    );
  });

  /*
   * Untagged, always drawn once the company's bank lines are in — even at
   * zero, and for the same reason the untagged BAR is always drawn. Its
   * absence would read as "everything is tagged", which is the one thing it
   * must not say without having checked. It has no tracked half by
   * definition: a Daily Report always names its job.
   */
  const untaggedRow = $derived.by((): JobComparison | null => {
    if (allTimeSiteId !== null || bankedAllTime === null) return null;
    const row = bankedAllTime.by_site.find((site) => site.site_id === null);
    return row ? compareJob(null, bankedOf(row), null, 'Untagged') : null;
  });

  /** The register as the table shows it: every job, then Untagged at the foot. */
  const register = $derived(untaggedRow ? [...jobRegister, untaggedRow] : jobRegister);

  /**
   * The scope's own two books. In company scope the costed side is every
   * job's Daily Reports added up — jobs only, which is all /admin/sites holds
   * — while the cleared side is every imported line, tagged or not; the
   * Untagged row at the foot of the register is where that difference shows.
   *
   * Both sides are ex-GST. The costed book has no GST in it at all, and GST
   * is collected for the government rather than earned, so comparing an
   * as-printed deposit against a costed revenue would put the government's
   * share on one side of the subtraction only.
   */
  const comparison = $derived.by((): JobComparison => {
    const cleared: BankedFigures | null =
      bankedAllTime === null || !bankedAllTime.lines.some((line) => line.kind !== 'transfer')
        ? null
        : {
            money_in: bankedAllTime.income_ex_gst,
            money_out: bankedAllTime.spend_ex_gst,
            net: bankedAllTime.net
          };

    if (allTimeSiteId === null) return compareJob(companyTracked, cleared, null, 'Company (all)');

    const site = trackedAllTime?.sites.find((row) => row.id === allTimeSiteId) ?? null;
    return compareJob(
      site ? trackedOf(site) : null,
      cleared,
      allTimeSiteId,
      site?.name ?? jobName(allTimeSiteId)
    );
  });

  const importPath = $derived.by(() => {
    const shown = loadedPeriod ?? period;
    return routePath.adminExpenses('month' in shown ? shown.month : `${shown.year}-01`);
  });
</script>

<AdminHero kicker="Admin · Two books" title="Total Breakdown" {stats}>
  {#snippet links()}
    <a class="a-chip" href={routePath.admin()} onclick={linkHandler}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 12H5m7-7-7 7 7 7" /></svg>
      Day-to-day concrete figures are on the Performance Dashboard
    </a>
  {/snippet}
</AdminHero>

<p class="source">
  Two books on one screen, and never one figure: what your Daily Reports costed, and what actually
  moved through the bank. Every section says which of the two it is reading — nothing here adds
  them together, so nothing is counted twice.
</p>

<div class="scopebar">
  <div class="scope">
    <!--
      No role="tablist": these are two toggles, not tabs, and a tablist whose
      children are not role="tab" is announced as an empty tab list. The pair
      of aria-pressed buttons is already correct on its own.
    -->
    <div class="scope__switch">
      <button type="button" aria-pressed={!askedIsYear} onclick={toMonth}>Month</button>
      <button type="button" aria-pressed={askedIsYear} onclick={toYear}>Year</button>
    </div>
    <label class="scope__field">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10h18M8 3v4M16 3v4" />
      </svg>
      {#if 'year' in period}
        <input
          type="number"
          min="2000"
          max="2999"
          step="1"
          inputmode="numeric"
          aria-label="Year"
          value={period.year}
          onchange={(e) => setYear((e.currentTarget as HTMLInputElement).value)}
        />
      {:else}
        <input
          type="month"
          aria-label="Month"
          value={period.month}
          onchange={(e) => setMonth((e.currentTarget as HTMLInputElement).value)}
        />
      {/if}
    </label>
  </div>

  <label class="sitepick">
    <span class="visually-hidden">Job site</span>
    <select value={siteId === null ? '' : String(siteId)} onchange={(e) => setSite((e.currentTarget as HTMLSelectElement).value)}>
      <option value="">Company (all)</option>
      {#each pickableSites as site (site.site_id)}
        <option value={String(site.site_id)}>{site.name}</option>
      {/each}
    </select>
  </label>

  {#if loading && banked !== null}
    <span class="scopebar__busy"><span class="spinner" aria-hidden="true"></span> Updating…</span>
  {/if}
</div>

<!-- ---- 1 · what we costed ---------------------------------------------- -->
<TrackedSection
  months={tracked?.months ?? []}
  figures={trackedFigures}
  period={trackedLabel}
  scope={trackedScopeName}
  isYear={trackedIsYear}
  onretry={retry}
  loading={trackedLoading}
  failed={trackedFailed}
/>

<!-- ---- 2 · what actually moved ------------------------------------------
  The band, and not just the panels' own subtitles, because this is where the
  screen changes books. Everything above it is priced out of Daily Reports;
  everything from here to the compare section is an imported bank line and
  nothing else. The four panels underneath each repeat the period and the
  scope in their own subtitle — none of them is ever read out of context of
  its source.
-->
<div class="band">
  <h2 class="band__title">Bank statements</h2>
  <p class="band__sub">What actually moved · {loadedLabel} · {siteName}</p>
</div>

{#if loading && banked === null}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if banked === null}
  <!--
    Every path where there are no figures lands here, so the region can never
    render blank: a first load that failed, and a scope change whose request
    failed and took the previous period's figures with it.
  -->
  <div class="empty">
    <strong>Could not load {loadedLabel}</strong>
    <span class="meta">
      {loadFailed
        ? 'The figures for this period did not come back. Nothing is wrong with them — the request failed. Try again, or check the connection.'
        : 'The figures for this period have not arrived yet.'}
    </span>
    <button class="a-cta" type="button" onclick={retry} disabled={loading}>
      {loading ? 'Trying…' : 'Try again'}
    </button>
  </div>
{:else}
  {#if nothingHere}
    <!--
      Never a grid of zeros: $0.00 everywhere reads as a period in which the
      company did nothing, when what it means is that no statement covering it
      has been imported yet. So: say that, and give the way to fix it.
    -->
    <div class="empty">
      <strong>No statement covers {loadedLabel}{loadedSiteId === null ? '' : ` for ${siteName}`}</strong>
      <span class="meta">
        This screen only ever shows imported bank lines, so until a statement covering
        {loadedLabel} is in, there is nothing here to show.
      </span>
      <a class="a-cta" href={importPath} onclick={linkHandler}>Import a statement</a>
      <span class="meta">On the Expenses screen, under Import.</span>
    </div>
  {:else}
    <!--
      GST is collected for the government rather than earned, so it is in the
      net above but in neither of the two figures beside it. A sunken aside
      rather than a card, so it can never be read as part of the result.
    -->
    <section class="gst" aria-labelledby="gst-title">
      <h2 class="gst__title" id="gst-title">
        <svg viewBox="0 0 24 24" aria-hidden="true"
          ><path
            d="M12 8v8m-3-5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 0 3h-3a1.5 1.5 0 0 0 0 3h3a1.5 1.5 0 0 0 1.5-1.5"
          /><circle cx="12" cy="12" r="9" /></svg
        >
        GST
        <span>money in and money out are as printed, GST included — the net and every chart are ex-GST</span>
      </h2>
      <dl class="gst__figs">
        <div><dt>Collected</dt><dd class="a-num">{money(banked.gst_collected)}</dd></div>
        <div><dt>Paid</dt><dd class="a-num">{money(banked.gst_paid)}</dd></div>
        <div><dt>Net owing</dt><dd class="a-num">{money(banked.gst_net)}</dd></div>
      </dl>
    </section>

    <!-- ---- where it went ------------------------------------------------ -->
    <section class="panel" aria-labelledby="cat-title">
      <div class="panel__head">
        <h2 class="panel__title" id="cat-title">Where it went</h2>
        <p class="panel__sub">{loadedLabel} · {siteName} · ex-GST</p>
      </div>
      {#if slices.length === 0}
        <p class="meta">Nothing was spent in {loadedLabel} — only deposits or transfers.</p>
      {:else}
        <div class="panel__split">
          <DonutChart
            {slices}
            total={money(banked.spend_ex_gst)}
            caption="spent, ex-GST"
            selected={selectedCategory}
            onselect={(key) => (selectedCategory = key)}
          />
          <ChartLegend
            items={slices}
            selected={selectedCategory}
            onselect={(key) => (selectedCategory = key)}
          />
        </div>
        <p class="panel__foot">Pick a category to narrow the ledger below to the payments inside it.</p>
      {/if}
    </section>

    <!-- ---- month by month ----------------------------------------------- -->
    <section class="panel" aria-labelledby="trend-title">
      <div class="panel__head">
        <h2 class="panel__title" id="trend-title">Month by month</h2>
        <p class="panel__sub">
          {loadedIsYear
            ? `The twelve months of ${loadedLabel}`
            : `The twelve months ending ${loadedLabel}`} · {siteName} · ex-GST
        </p>
      </div>
      <div class="serieskey">
        {#each SERIES as se, i (se.key)}
          <span class="serieskey__item">
            <span class="chart-swatch" style:background="var(--chart-{i + 1})" aria-hidden="true"></span>
            {se.label}
          </span>
        {/each}
        <span class="serieskey__item">
          <span class="serieskey__line" aria-hidden="true"></span>
          Net
        </span>
      </div>
      <BarChart
        groups={trendGroups}
        series={SERIES}
        caption="Money in and money out by month, with net"
        lineLabel="Net"
        tickLabel={moneyAxis}
      />
    </section>

    <!-- ---- by job site --------------------------------------------------- -->
    <section class="panel" aria-labelledby="site-title">
      <div class="panel__head">
        <h2 class="panel__title" id="site-title">By job site</h2>
        <p class="panel__sub">{loadedLabel} · every site · ex-GST</p>
      </div>

      {#if untaggedIsLoud}
        <!--
          Above the chart, not below it and not only as a bar: an untagged
          share this large means tagging slipped at import, and the owner
          should read that before he reads the sites it distorts.
        -->
        <p class="flag">
          <span class="pill pill--warn">
            {percent(untaggedShare)} of the company's dollars in this period have no job site. Tag
            them at import to see them here.
          </span>
        </p>
      {/if}

      {#if loadedSiteId !== null}
        <p class="panel__note">
          Every site is drawn here for comparison — the {siteName} filter above narrows the figures and the
          two charts before this one, but not this one.
        </p>
      {/if}

      <div class="serieskey">
        {#each SERIES as se, i (se.key)}
          <span class="serieskey__item">
            <span class="chart-swatch" style:background="var(--chart-{i + 1})" aria-hidden="true"></span>
            {se.label}
          </span>
        {/each}
        <span class="serieskey__item">
          <span class="serieskey__line" aria-hidden="true"></span>
          Net
        </span>
      </div>
      <BarChart
        groups={siteGroups}
        series={SERIES}
        caption="Money in and money out by job site, with net"
        lineLabel="Net"
        tickLabel={moneyAxis}
      />
    </section>

    <!-- ---- the ledger ---------------------------------------------------- -->
    <section class="panel" aria-labelledby="ledger-title">
      <div class="panel__head">
        <h2 class="panel__title" id="ledger-title">Every line</h2>
        <p class="panel__sub">
          {ledgerRows.length} line{ledgerRows.length === 1 ? '' : 's'} · {loadedLabel} · {siteName} · as printed
        </p>
      </div>

      {#if selectedCategory !== null}
        <p class="filterbar">
          Showing <b>{selectedCategoryName}</b> only.
          <button type="button" class="btn btn--sm" onclick={() => (selectedCategory = null)}>
            Show everything
          </button>
        </p>
      {/if}

      {#if ledgerRows.length === 0}
        <p class="meta">Nothing here under {selectedCategoryName ?? loadedLabel}.</p>
      {:else}
        <div class="tablewrap">
          <table>
            <caption class="visually-hidden">
              Every deposit and payment in {loadedLabel}
            </caption>
            <thead>
              <tr>
                <th scope="col" aria-sort={ariaSort('date')}>
                  <button type="button" class="sorter" onclick={() => sortBy('date')}>
                    Date{#if sortKey === 'date'}<span aria-hidden="true">{sortDesc ? ' ↓' : ' ↑'}</span>{/if}
                  </button>
                </th>
                <th scope="col">Source or merchant</th>
                <th scope="col">Category</th>
                <th scope="col">Job site</th>
                <th scope="col" class="num" aria-sort={ariaSort('amount')}>
                  <button type="button" class="sorter sorter--num" onclick={() => sortBy('amount')}>
                    Amount{#if sortKey === 'amount'}<span aria-hidden="true">{sortDesc ? ' ↓' : ' ↑'}</span>{/if}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {#each ledgerRows as line (`${line.kind}:${line.id}`)}
                <tr>
                  <td class="a-num">{line.date}</td>
                  <td class="wrap">
                    {line.label || '—'}
                    {#if line.description}<small>{line.description}</small>{/if}
                  </td>
                  <td>
                    {#if line.kind === 'income'}
                      <span class="tag tag--in">Deposit</span>
                    {:else}
                      {line.category}
                    {/if}
                  </td>
                  <td>{line.site ?? 'Untagged'}</td>
                  <td class="num" class:is-in={line.kind === 'income'}>
                    {line.kind === 'income' ? '+' : '−'}{money(line.amount_total)}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}

      <!--
        Transfers are recorded and shown, but never counted. They sit behind a
        disclosure headed with their own total so the money is one tap from
        visible without ever sitting inside a spending figure.
      -->
      <details class="transfers">
        <summary>
          Transfers (excluded) — <b class="a-num">{money(banked.transfers_total)}</b>
          <span class="transfers__count">
            {transferLines.length} line{transferLines.length === 1 ? '' : 's'}
          </span>
        </summary>
        <p class="transfers__why">
          Money moved between your own accounts, not money spent. Counting a card payment would double
          what the card already bought.
        </p>
        {#if transferLines.length === 0}
          <p class="meta">No transfers in {loadedLabel}.</p>
        {:else}
          <div class="tablewrap">
            <table>
              <caption class="visually-hidden">Transfers in {loadedLabel}, excluded from every total</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Merchant</th>
                  <th scope="col">Category</th>
                  <th scope="col" class="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {#each transferLines as line (line.id)}
                  <tr>
                    <td class="a-num">{line.date}</td>
                    <td class="wrap">{line.label || '—'}</td>
                    <td>{line.category}</td>
                    <td class="num">{money(line.amount_total)}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </details>
    </section>
  {/if}
{/if}

<!-- ---- 3 · the two books, side by side ---------------------------------- -->
<CompareSection
  {comparison}
  {register}
  period={allTimeLabel}
  scope={allTimeScopeName}
  isJobScope={allTimeSiteId !== null}
  onpick={(id) => go(period, id)}
  onretry={retry}
  loading={allTimeLoading}
  failed={allTimeFailed}
/>

<!-- ---- 4 · which jobs are doing best ------------------------------------
  Company scope only. With one job picked there is nothing to rank it
  against, and a ranking of one reads as a verdict.

  Gated on `siteId` — what was ASKED for — rather than `allTimeSiteId`,
  which is read off the response and is therefore null (the company-wide
  value) for as long as a job-scoped request is in flight or has failed.
  Unlike a heading, "should this section exist at all" is not something
  that should lag the fetch: gating it on the response let the section
  flash into view in job scope on every load, and stay visible if the
  request never came back.
-->
{#if siteId === null}
  <JobComparisonSection
    rows={jobRegister}
    period={allTimeLabel}
    onretry={retry}
    loading={allTimeLoading}
    failed={allTimeFailed}
  />
{/if}

<style>
  /* The sentence that keeps the two views apart. Rail rather than a pill: it
     has to read as a standing statement about the screen, not as a warning
     that could be dismissed. */
  .source {
    margin: 0 0 0.9rem;
    padding: 0.5rem 0.75rem;
    border-left: 4px solid var(--accent);
    background: var(--paper);
    border-radius: 0 var(--radius) var(--radius) 0;
    box-shadow: var(--shadow);
    font-size: 0.86rem;
    color: var(--ink-soft);
  }

  .scopebar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem 0.8rem;
    padding: 0.6rem 0.75rem;
    margin-bottom: 0.9rem;
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
  }
  .scopebar__busy {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }

  /* The dashboard's Day|Month control, wearing Month|Year — one control in two
     places should not be two different shapes. */
  .scope {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
  }
  .scope__switch {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    flex: 0 0 auto;
    width: 11.5rem;
    border-radius: 999px;
    background: var(--bg-sunken);
  }
  .scope__switch button {
    min-height: var(--tap);
    padding: 0 0.5rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    font: inherit;
    font-size: 0.88rem;
    font-weight: 700;
    color: var(--ink-soft);
    cursor: pointer;
    touch-action: manipulation;
    transition: background 150ms ease-out, color 150ms ease-out;
  }
  .scope__switch button:hover { color: var(--ink); }
  .scope__switch button[aria-pressed='true'] {
    background: var(--brand);
    color: #fff;
    box-shadow: var(--shadow);
  }
  .scope__switch button:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }

  .scope__field {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    flex: 1 1 8rem;
    min-width: 0;
    max-width: 12rem;
    min-height: var(--tap);
    padding: 0 0.75rem;
    border: 1.5px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
  }
  .scope__field:focus-within {
    border-color: var(--brand);
    box-shadow: 0 0 0 3px var(--brand-tint);
  }
  .scope__field svg {
    width: 1.05rem;
    height: 1.05rem;
    flex: 0 0 auto;
    fill: none;
    stroke: var(--ink-faint);
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .scope__field input {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    min-height: calc(var(--tap) - 4px);
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--ink);
    appearance: none;
  }
  .scope__field input:focus-visible { outline: none; }

  .sitepick {
    display: flex;
    flex: 1 1 10rem;
    min-width: 0;
    max-width: 16rem;
  }
  .sitepick select {
    width: 100%;
    min-height: var(--tap);
    padding: 0 0.75rem;
    border: 1.5px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
    font: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--ink);
  }
  .sitepick select:focus-visible {
    outline: none;
    border-color: var(--brand);
    box-shadow: 0 0 0 3px var(--brand-tint);
  }

  /* The line where the screen changes books. Wider and quieter than a panel
     title so it reads as a heading OVER the four bank panels rather than as a
     fifth panel beside them. */
  .band {
    display: grid;
    gap: 0.1rem;
    margin: 1.2rem 0 0.6rem;
    padding-bottom: 0.4rem;
    border-bottom: 2px solid var(--line-soft);
  }
  .band__title {
    margin: 0;
    font-size: 1.05rem;
    letter-spacing: -0.01em;
  }
  .band__sub {
    margin: 0;
    font-size: 0.76rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-faint);
  }

  /* ---- GST aside --------------------------------------------------------- */
  .gst {
    display: grid;
    gap: 0.5rem;
    padding: 0.75rem 0.9rem;
    margin-bottom: 0.9rem;
    background: var(--bg-sunken);
    border-radius: var(--radius-lg);
  }
  .gst__title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem 0.5rem;
    margin: 0;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-soft);
  }
  .gst__title svg {
    width: 1.1rem;
    height: 1.1rem;
    fill: none;
    stroke: var(--ink-faint);
    stroke-width: 2;
    stroke-linecap: round;
  }
  .gst__title span {
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    color: var(--ink-faint);
  }
  .gst__figs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(7rem, 1fr));
    gap: 0.4rem 1rem;
    margin: 0;
  }
  .gst__figs div { display: grid; gap: 0.05rem; }
  .gst__figs dt {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-faint);
  }
  .gst__figs dd {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
  }

  /* ---- panels ------------------------------------------------------------ */
  .panel {
    display: grid;
    gap: 0.7rem;
    padding: 0.9rem;
    margin-bottom: 0.9rem;
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
  }
  .panel__head { display: grid; gap: 0.1rem; }
  .panel__title {
    margin: 0;
    font-size: 1.05rem;
    letter-spacing: -0.01em;
  }
  .panel__sub {
    margin: 0;
    font-size: 0.76rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-faint);
  }
  .panel__foot,
  .panel__note {
    margin: 0;
    font-size: 0.82rem;
    color: var(--ink-soft);
  }

  /* Ring and key side by side once there is room, stacked before that — the
     legend is the readable half on a phone, so it never gets squeezed into a
     column beside a 15rem ring. */
  .panel__split {
    display: grid;
    gap: 0.8rem;
    align-items: center;
  }
  @media (min-width: 34rem) {
    .panel__split { grid-template-columns: minmax(0, 15rem) minmax(0, 1fr); }
  }

  .flag { margin: 0; }

  /* What each bar colour means. BarChart draws no legend of its own — it has
     no formatted strings to put in one — so the key is built here from the
     same ramp variables the bars read. */
  .serieskey {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem 0.9rem;
    margin: 0;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--ink-soft);
  }
  .serieskey__item {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }
  .serieskey__line {
    display: inline-block;
    width: 0.9rem;
    height: 0;
    border-top: 2px solid var(--brand-dark);
    flex: 0 0 auto;
  }

  /* ---- the ledger -------------------------------------------------------- */
  .filterbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem 0.6rem;
    margin: 0;
    padding: 0.4rem 0.6rem;
    border-radius: var(--radius);
    background: var(--brand-tint);
    font-size: 0.86rem;
  }

  .sorter {
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    color: inherit;
    text-transform: inherit;
    letter-spacing: inherit;
    cursor: pointer;
    touch-action: manipulation;
  }
  .sorter:hover { color: var(--ink); }
  .sorter:focus-visible {
    outline: 2px solid var(--brand);
    outline-offset: 2px;
  }
  .sorter--num { justify-content: flex-end; width: 100%; }

  /* The one column allowed to breathe: a merchant is the widest thing here and
     truncating it loses the only word saying what the line was. */
  .wrap {
    white-space: normal;
    min-width: 9rem;
  }
  .wrap small {
    display: block;
    font-size: 0.76rem;
    color: var(--ink-faint);
  }

  .tag {
    display: inline-block;
    padding: 0 0.4rem;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .tag--in {
    background: var(--ok-tint);
    color: var(--ok);
  }

  /* Money in is the only figure on this screen that is unambiguously good
     news, and it is the only one that carries a colour. */
  td.is-in { color: var(--ok); font-weight: 700; }

  .transfers {
    padding: 0.5rem 0.6rem;
    border-radius: var(--radius);
    background: var(--bg-sunken);
  }
  .transfers summary {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.3rem 0.5rem;
    min-height: var(--tap);
    font-size: 0.86rem;
    font-weight: 700;
    color: var(--ink-soft);
    cursor: pointer;
    touch-action: manipulation;
  }
  .transfers summary:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }
  .transfers__count {
    font-weight: 400;
    color: var(--ink-faint);
  }
  .transfers__why {
    margin: 0.2rem 0 0.6rem;
    font-size: 0.82rem;
    font-weight: 400;
    color: var(--ink-soft);
  }

  .empty {
    display: grid;
    gap: 0.5rem;
    justify-items: start;
    background: var(--paper);
    border-radius: var(--radius-lg);
    padding: 1.2rem;
    box-shadow: var(--shadow);
  }

  @media (prefers-reduced-motion: reduce) {
    .scope__switch button { transition: none; }
  }
</style>
